import { CommonModule } from '@angular/common';
import { Component, inject, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';

import { TranslocoModule } from '@jsverse/transloco';
import { toObservable } from '@angular/core/rxjs-interop';
import { finalize, Observable, Subject, takeUntil } from 'rxjs';

import { Company } from '@hmax-order/shared/types';
import { UserStateModel, UserStore } from '@hmax-order/frontend/state';
import { InputTextAutocompleteComponent } from '@hmax-order/frontend/ui';
import { CompanyService } from '../services';

@Component({
  selector: 'hmax-company-select',
  standalone: true,
  templateUrl: './company-select.component.html',
  styleUrls: ['./company-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, InputTextAutocompleteComponent, MatButtonModule, MatDialogModule, ReactiveFormsModule, TranslocoModule],
})
export class CompanySelectComponent implements OnInit, OnDestroy {
  private _unsubscribeAll: Subject<any> = new Subject<any>();
  private _userStore = inject(UserStore);
  private _formBuilder = inject(FormBuilder);
  private _dialogRef = inject(MatDialogRef<CompanySelectComponent>);
  private _companyService = inject(CompanyService);

  form!: FormGroup;
  isSuperUser = false;
  currentCompanyId?: string;
  companies: Partial<Company>[] = [];
  companyBadge!: (company: Partial<Company>) => { label: string; color: string; textColor: string } | null;
  currentCompany$: Observable<Partial<Company> | undefined> = toObservable(this._userStore.getCurrentCompany);
  roles$: Observable<string[] | undefined> = toObservable(this._userStore.getRoles);

  /**
   * Abrir um modal deste componente com as configurações padrão
   * Precisa receber uma instância do MatDialog
   * ex: CompanySelectComponent.open(this._dialog);
   * @param dialog Instância de MatDialog do Angular Material que deve ser injetada
   * pela classe que invocar este método
   */
  static open(dialog: MatDialog) {
    dialog.open(CompanySelectComponent, {
      panelClass: 'select-company-dialog',
      data: {},
      disableClose: false,
      autoFocus: true,
      width: '90vw',
      maxWidth: '480px',
    });
  }

  ngOnInit(): void {
    this.createForm();
    this.subscribeAttributes();
    this.loadCompanies();
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(null);
    this._unsubscribeAll.complete();
  }

  /** Criar formulário */
  private createForm(): void {
    this.form = this._formBuilder.group({
      company: [''],
    });
  }

  private loadCompanies(): void {
    this._companyService
      .getMeCompanies(this.isSuperUser)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: (companies) => (this.companies = companies),
      });
  }

  /**
   * Inscrever atributos que precisam obter valores de observáveis
   */
  private subscribeAttributes(): void {
    this.roles$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((roles: string[] | undefined) => {
      this.isSuperUser = roles?.includes('SUPER') ?? false;
    });

    this.currentCompany$?.pipe(takeUntil(this._unsubscribeAll)).subscribe((company: Partial<Company> | undefined) => {
      this.currentCompanyId = company?.id ?? '';
    });
  }

  /**
   * Evento acionado no click do botão "Selecionar"
   */
  onSelectClick(): void {
    const { id } = this.form.get('company')?.value as Partial<Company>;

    if (!id) {
      this.close();
      return;
    }

    this.form.disable();

    this.changeCurrentCompany(id);
  }

  /**
   * Altera currentCompany e token do usuário
   * @param currentCompanyId ID da empresa selecionada
   */
  changeCurrentCompany(currentCompanyId: string | null): void {
    this._companyService
      .patchCurrentCompany(currentCompanyId)
      .pipe(
        finalize(() => {
          this.close();
        }),
      )
      .subscribe({
        next: (response: { accessToken: string; user: UserStateModel }) => {
          const { currentCompanyId } = this._userStore.getUser();
          const isChangingCompany = currentCompanyId !== response.user.currentCompanyId;

          // Token PRIMEIRO. `setUser` muda `currentCompanyId`, e os recursos por
          // empresa dependem desse signal — qualquer flush entre as duas linhas
          // dispararia o refetch com o token da empresa anterior. Hoje o efeito só
          // roda na microtask seguinte, mas depender disso é depender de detalhe de
          // agendamento do Angular, não do código.
          localStorage.setItem('accessToken', response.accessToken);
          this._userStore.setUser(response.user);

          if (isChangingCompany) {
            // Reload duro: garante estado limpo na troca de empresa (era o efeito
            // colateral do @Action(SetUser) do NGXS, movido para aqui — quem troca
            // de empresa é quem decide navegar, não o setter de estado).
            // O token novo já está gravado acima, então o interceptor da próxima
            // requisição já lê a empresa nova.
            if (!response.user.currentCompanyId) {
              // Indo para a visão de organização: vai para a rota padrão de
              // empresas, para o caso de estar numa rota inexistente nesta visão.
              window.location.href = '/companies';
            } else {
              // Vai para a rota padrão da visão de uma empresa/hotel.
              window.location.href = '/reception';
            }
          }
        },
        error: (error) => {
          this.form.enable();
          throw error;
        },
      });
  }

  /**
   * Fechar este modal de seleção de empresa
   */
  close(): void {
    this._dialogRef.close();
  }
}
